/* Export a project's status report (PSR #14): the PDF report or the Excel workbook.

   One menu, two homes: an icon beside the close button in the job pop-up on Finance → Job
   Costs (variant "icon"), and an "Export" button on a project's Job Costs tab. "PDF report"
   opens the printable report in a new tab — print it, save it as a PDF or email it from
   there. "Excel workbook" builds the workbook right here (lib/psrData → lib/psrXlsx, loaded
   only when asked) and downloads it.

   Escape: while the menu is open it marks itself data-owns-escape, so inside the job pop-up
   one Escape closes the menu and the next closes the pop-up. */
import React, { useEffect, useRef, useState } from 'react';
import { JcIcon } from './JobCostShared';
import { exportPsrWorkbook, psrReportPath } from '../../lib/psrData';

const CSS = `
.psrx{position:relative;display:inline-flex}
.psrx-icon{width:32px;height:32px;border-radius:8px;display:inline-flex;align-items:center;justify-content:center;color:var(--text-3);background:var(--bg-surface);border:1px solid var(--border-default);box-shadow:var(--shadow-xs);cursor:pointer;transition:color .15s,border-color .15s,background .15s}
.psrx-btn{display:inline-flex;align-items:center;gap:6px;height:32px;padding:0 10px 0 13px;border-radius:8px;font-size:14px;font-weight:500;color:var(--text-2);background:var(--bg-surface);border:1px solid var(--border-default);box-shadow:var(--shadow-xs);cursor:pointer;transition:color .15s,border-color .15s,background .15s}
.psrx-icon:hover,.psrx-icon[aria-expanded="true"],.psrx-btn:hover,.psrx-btn[aria-expanded="true"]{color:var(--accent-text);border-color:color-mix(in oklch, var(--accent) 35%, var(--border-default));background:var(--accent-soft)}
.psrx-menu{position:absolute;top:calc(100% + 6px);right:0;z-index:30;width:272px;padding:6px;border-radius:10px;background:var(--bg-surface);border:1px solid var(--border-default);box-shadow:var(--shadow-md);text-align:left}
.psrx-head{font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--text-4);padding:5px 8px 6px}
.psrx-item{display:flex;gap:10px;align-items:flex-start;width:100%;padding:8px;border:0;border-radius:7px;background:none;text-align:left;cursor:pointer;color:var(--text-1);font:inherit}
.psrx-item:hover,.psrx-item:focus-visible{background:var(--bg-surface-2);outline:none}
.psrx-item[disabled]{cursor:progress}
.psrx-item b{display:block;font-size:13.5px;font-weight:600;line-height:18px}
.psrx-item span{display:block;font-size:12px;line-height:16px;color:var(--text-3);margin-top:1px}
.psrx-err{font-size:12px;line-height:16px;color:var(--danger);padding:6px 8px 4px}
`;
let cssInjected = false;
const injectCss = () => {
  if (cssInjected || typeof document === 'undefined') return;
  if (!document.getElementById('psrx-css')) {
    const s = document.createElement('style');
    s.id = 'psrx-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }
  cssInjected = true;
};

const Glyph = ({ color, children }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none', marginTop: 1 }}>
    {children}
  </svg>
);
// A page with lines (the report) and a page with a grid (the workbook).
const PdfGlyph = () => (
  <Glyph color="var(--danger)"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M9 13h6M9 17h4" /></Glyph>
);
const SheetGlyph = () => (
  <Glyph color="var(--success)"><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M4 10h16M4 15h16M10 4v16" /></Glyph>
);

export const PsrExportMenu = ({ project, variant = 'button' }) => {
  injectCss();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const firstRef = useRef(null);

  // Open: focus the first choice (so Escape and the arrow keys land here, not on the page),
  // and close on a click anywhere else.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    const raf = requestAnimationFrame(() => { if (firstRef.current) firstRef.current.focus(); });
    return () => { document.removeEventListener('mousedown', onDown); cancelAnimationFrame(raf); };
  }, [open]);

  if (!project || !(project.slug || project.id)) return null;

  const close = () => {
    setOpen(false);
    setError('');
    requestAnimationFrame(() => { if (triggerRef.current) triggerRef.current.focus(); });
  };
  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'Escape') { e.stopPropagation(); close(); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const items = [...rootRef.current.querySelectorAll('.psrx-item')];
      const at = items.indexOf(document.activeElement);
      const next = items[(at + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length];
      if (next) { e.preventDefault(); next.focus(); }
    }
  };
  const openPdf = () => {
    window.open(psrReportPath(project), '_blank', 'noopener');
    close();
  };
  const downloadExcel = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await exportPsrWorkbook(project);
      close();
    } catch (e) {
      setError(`The workbook couldn’t be built: ${e.message || 'unknown error'}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="psrx" ref={rootRef} onKeyDown={onKeyDown} data-owns-escape={open ? 'true' : undefined}>
      {variant === 'icon' ? (
        <button type="button" ref={triggerRef} className="psrx-icon" onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu" aria-expanded={open} aria-label="Export this job" title="Export">
          <JcIcon name="download" size={16} sw={1.8} />
        </button>
      ) : (
        <button type="button" ref={triggerRef} className="psrx-btn" onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu" aria-expanded={open}>
          <JcIcon name="download" size={13} /> Export <JcIcon name="chevron-d" size={14} />
        </button>
      )}
      {open && (
        <div className="psrx-menu" role="menu" aria-label="Export this job">
          <div className="psrx-head">Export this job</div>
          <button type="button" role="menuitem" className="psrx-item" ref={firstRef} onClick={openPdf}>
            <PdfGlyph />
            <div><b>PDF report</b><span>Opens the printable report to print, save or email</span></div>
          </button>
          <button type="button" role="menuitem" className="psrx-item" onClick={downloadExcel} disabled={busy} aria-busy={busy}>
            <SheetGlyph />
            <div><b>{busy ? 'Building the workbook…' : 'Excel workbook'}</b><span>Downloads the .xlsx to work with</span></div>
          </button>
          {error && <div className="psrx-err" role="alert">{error}</div>}
        </div>
      )}
    </div>
  );
};

export default PsrExportMenu;
